import ModalCloseButton from '../../../Component/UI/ModalCloseButton';
import SearchSubmitButton from "../../../Component/UI/SearchSubmitButton";
import { sellerTable } from "../../../Component/style/sellerTable";
import { FormEvent, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import api from "../../../Component/axios_Interceptor/api";
import url from "../../../Component/server";
import Layout_Admin from "../../../Component/UI/Layout_Admin";
import AdminPageHeader, { AdminActionButton } from "../../../Component/UI/AdminPageHeader";
import { SELLER_ADMIN_PERMISSION_GROUPS, SellerAdminPermissionKey } from "../../../Component/adminPermissions";

type Status = "active" | "leave" | "retired";
type AccountStatus = "active" | "inactive";
type PermissionRules = Record<SellerAdminPermissionKey, boolean>;
type Option = { seq: number; name: string; permissions?: PermissionRules };
type StaffAccount = {
  seq: number; userid: string; name: string; nickname: string; phone: string | null; user_level: "master" | "cs";
  team_seq: number | null; team_name: string | null; permission_seq: number | null; permission_name: string;
  employment_status: Status;
};

const staffUrl = url.Communication_url.seller_staff;
const settingsUrl = url.Communication_url.seller_staff_settings;
const statusLabel: Record<AccountStatus, string> = { active: "활성", inactive: "비활성" };
// Keep legacy API values while presenting one inactive state in account management.
const accountStatus = (status: Status): AccountStatus => status === "active" ? "active" : "inactive";
const emptyCreate = { userid: "", userpw: "", name: "", phone: "", team_seq: "", permission_seq: "" };
const phoneDigits = (value: string) => value.replace(/[^0-9]/g, "");
const emptyPermissionRules = () => Object.fromEntries(
  SELLER_ADMIN_PERMISSION_GROUPS.flatMap(group => group.items.map(item => [item.key, false])),
) as PermissionRules;
const errorText = (error: any) => {
  const value = error?.response?.data?.message;
  return Array.isArray(value) ? value.join(" ") : value || "요청을 처리하지 못했습니다.";
};

export default function SellerStaffAccounts() {
  const [items, setItems] = useState<StaffAccount[]>([]);
  const [teams, setTeams] = useState<Option[]>([]);
  const [permissions, setPermissions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [modal, setModal] = useState<"" | "create" | "edit" | "settings" | "reset">("");
  const [selected, setSelected] = useState<StaffAccount | null>(null);
  const [createForm, setCreateForm] = useState(emptyCreate);
  const [editForm, setEditForm] = useState({ name: "", phone: "", team_seq: "", permission_seq: "", employment_status: "active" as Status, new_password: "" });
  const [searchField, setSearchField] = useState<"name" | "userid">("name");
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [permissionFilter, setPermissionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sort, setSort] = useState<{ key: "permission" | "team" | "status"; direction: 1 | -1 } | null>(null);
  const [page, setPage] = useState(1);
  const [settingTab, setSettingTab] = useState<"team" | "permission">("team");
  const [settingName, setSettingName] = useState("");
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [checkingId, setCheckingId] = useState(false);
  const [idCheck, setIdCheck] = useState<{ userid: string; message: string; available: boolean } | null>(null);
  const [currentPermissions, setCurrentPermissions] = useState<string[]>([]);
  const [selectedPermissionSeq, setSelectedPermissionSeq] = useState<number | null>(null);
  const [permissionName, setPermissionName] = useState("");
  const [permissionDraft, setPermissionDraft] = useState<PermissionRules>(emptyPermissionRules);

  const load = async () => {
    setLoading(true);
    try {
      const [response, userResponse] = await Promise.all([api.get(staffUrl), api.get(url.Communication_url.user_info)]);
      if (!Array.isArray(response.data?.items)) throw new Error("invalid account list");
      setItems(response.data.items);
      setTeams(response.data.teams || []);
      setPermissions(response.data.permissions || []);
      setCurrentPermissions(userResponse.data?.admin_permissions || []);
      setError("");
    } catch (cause) { setError(errorText(cause)); }
    finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, []);
  useEffect(() => { setPage(1); }, [search, searchField, teamFilter, permissionFilter, statusFilter, sort]);
  useEffect(() => {
    if (settingTab !== "permission" || !permissions.length) return;
    const selectedOption = permissions.find(option => option.seq === selectedPermissionSeq) || permissions[0];
    if (selectedOption.seq !== selectedPermissionSeq) setSelectedPermissionSeq(selectedOption.seq);
    setPermissionName(selectedOption.name);
    setPermissionDraft({ ...emptyPermissionRules(), ...(selectedOption.permissions || {}) });
  }, [settingTab, permissions, selectedPermissionSeq]);
  useEffect(() => {
    if (!modal) return;
    const keydown = (event: KeyboardEvent) => { if (event.key === "Escape" && !saving) setModal(""); };
    window.addEventListener("keydown", keydown);
    return () => window.removeEventListener("keydown", keydown);
  }, [modal, saving]);

  const filtered = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    const result = items.filter(item => (!keyword || String(item[searchField] || "").toLowerCase().includes(keyword))
      && (!teamFilter || String(item.team_seq || "") === teamFilter)
      && (!permissionFilter || (permissionFilter === "master" ? item.user_level === "master" : String(item.permission_seq || "") === permissionFilter))
      && (!statusFilter || accountStatus(item.employment_status) === statusFilter));
    if (!sort) return result;
    const value = (item: StaffAccount) => sort.key === "permission" ? item.permission_name : sort.key === "team" ? item.team_name || "" : statusLabel[accountStatus(item.employment_status)];
    return [...result].sort((a, b) => value(a).localeCompare(value(b), "ko") * sort.direction);
  }, [items, search, searchField, teamFilter, permissionFilter, statusFilter, sort]);
  const pages = Math.max(1, Math.ceil(filtered.length / 10));
  const visible = filtered.slice((page - 1) * 10, page * 10);

  const mutate = async (action: () => Promise<any>, message: string, keepModal = false) => {
    if (saving) return;
    setSaving(true); setError(""); setNotice("");
    try {
      await action();
      if (!keepModal) setModal("");
      setNotice(message);
      await load();
      setSettingName("");
    } catch (cause) { setError(errorText(cause)); }
    finally { setSaving(false); }
  };

  const create = (event: FormEvent) => {
    event.preventDefault();
    void mutate(() => api.post(staffUrl, {
      userid: createForm.userid, userpw: createForm.userpw, name: createForm.name, phone: phoneDigits(createForm.phone), role: "cs",
      ...(createForm.team_seq ? { team_seq: Number(createForm.team_seq) } : {}),
      ...(createForm.permission_seq ? { permission_seq: Number(createForm.permission_seq) } : {}),
    }), "계정을 생성했습니다.");
  };

  const checkId = async () => {
    const userid = createForm.userid.trim();
    if (!userid || checkingId) return;
    setCheckingId(true);
    try {
      await api.get(url.Communication_url.id_dup_check, { params: { userid } });
      setIdCheck({ userid, message: "사용 가능한 아이디입니다.", available: true });
    } catch (cause) {
      setIdCheck({ userid, message: errorText(cause), available: false });
    } finally { setCheckingId(false); }
  };

  const openEdit = (item: StaffAccount) => {
    setSelected(item); setError(""); setTemporaryPassword("");
    setEditForm({ name: item.name, phone: item.phone || "", team_seq: String(item.team_seq || ""), permission_seq: String(item.permission_seq || ""), employment_status: item.employment_status, new_password: "" });
    setModal("edit");
  };

  const saveEdit = (event: FormEvent) => {
    event.preventDefault(); if (!selected) return;
    void mutate(() => api.patch(`${staffUrl}/${selected.seq}`, selected.user_level === "master"
      ? { name: editForm.name, phone: phoneDigits(editForm.phone), ...(editForm.new_password ? { new_password: editForm.new_password } : {}) }
      : { name: editForm.name, phone: phoneDigits(editForm.phone), team_seq: editForm.team_seq ? Number(editForm.team_seq) : null,
        permission_seq: editForm.permission_seq ? Number(editForm.permission_seq) : null,
        employment_status: editForm.employment_status, ...(editForm.new_password ? { new_password: editForm.new_password } : {}) }), "계정 정보를 저장했습니다.");
  };

  const resetPassword = async () => {
    if (!selected || saving) return;
    setSaving(true); setError("");
    try {
      const response = await api.post(`${staffUrl}/${selected.seq}/reset-password`);
      setTemporaryPassword(response.data?.temporary_password || "");
    } catch (cause) { setError(errorText(cause)); }
    finally { setSaving(false); }
  };

  const saveOption = () => {
    const name = settingName.trim(); if (!name) return;
    void mutate(() => api.post(`${settingsUrl}/${settingTab}`, { name }), `${settingTab === "team" ? "팀" : "권한"}을 추가했습니다.`, true);
  };
  const selectPermission = (option: Option) => {
    setSelectedPermissionSeq(option.seq);
    setPermissionName(option.name);
    setPermissionDraft({ ...emptyPermissionRules(), ...(option.permissions || {}) });
  };
  const savePermission = () => {
    const name = permissionName.trim();
    if (!selectedPermissionSeq || !name) return;
    void mutate(() => api.patch(`${settingsUrl}/permission/${selectedPermissionSeq}`, { name, permissions: permissionDraft }), "권한 설정을 저장했습니다.", true);
  };
  const deleteOption = (option: Option) => {
    if (!window.confirm(`'${option.name}' 설정을 삭제하시겠습니까? 연결된 계정은 미지정으로 변경됩니다.`)) return;
    void mutate(() => api.delete(`${settingsUrl}/${settingTab}/${option.seq}`), "설정을 삭제했습니다.", true);
  };
  const toggleSort = (key: "permission" | "team" | "status") => setSort(current => current?.key === key ? { key, direction: current.direction === 1 ? -1 : 1 } : { key, direction: 1 });
  const canManageAccounts = currentPermissions.includes("account_manage");
  const canManagePermissions = currentPermissions.includes("account_permission");

  return <Layout_Admin menuname="계정 관리">
    <Shell>
      <AdminPageHeader title="계정 관리" description="관리자 계정과 팀·권한을 설정합니다." actions={<>
        {canManagePermissions && <AdminActionButton type="button" onClick={() => { setError(""); setSettingName(""); setModal("settings"); }}>⚙ 팀/권한 설정</AdminActionButton>}
        {canManageAccounts && <AdminActionButton $primary type="button" onClick={() => { setCreateForm({ ...emptyCreate, permission_seq: canManagePermissions && permissions[0] ? String(permissions[0].seq) : "" }); setIdCheck(null); setError(""); setModal("create"); }}>계정 생성</AdminActionButton>}
      </>} />
      <Toolbar>
        <Count>전체 <strong>{filtered.length}</strong></Count>
        <Filters role="group" aria-label="계정 필터">
          <select aria-label="팀 필터" value={teamFilter} onChange={event => setTeamFilter(event.target.value)}><option value="">전체 팀</option>{teams.map(team => <option key={team.seq} value={team.seq}>{team.name}</option>)}</select>
          <select aria-label="권한 필터" value={permissionFilter} onChange={event => setPermissionFilter(event.target.value)}><option value="">전체 권한</option><option value="master">관리자</option>{permissions.map(permission => <option key={permission.seq} value={permission.seq}>{permission.name}</option>)}</select>
          <select aria-label="상태 필터" value={statusFilter} onChange={event => setStatusFilter(event.target.value)}><option value="">전체 상태</option>{Object.entries(statusLabel).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select>
        </Filters>
        <SearchGroup data-admin-search="" role="search" aria-label="계정 검색" onSubmit={event => { event.preventDefault(); setSearch(searchDraft.trim()); setPage(1); }} onKeyDown={event => { if (event.key === "Enter" && (event.nativeEvent.isComposing || event.keyCode === 229)) event.preventDefault(); }}>
          <select aria-label="검색 기준" value={searchField} onChange={event => setSearchField(event.target.value as "name" | "userid")}><option value="name">이름</option><option value="userid">아이디</option></select>
          <SearchBox><span aria-hidden="true">⌕</span><input aria-label="계정 검색" value={searchDraft} onChange={event => setSearchDraft(event.target.value)} enterKeyHint="search" placeholder={searchField === "name" ? "이름을 입력해주세요." : "아이디를 입력해주세요."} /></SearchBox>
          <SearchSubmitButton type="submit">검색</SearchSubmitButton>
        </SearchGroup>
      </Toolbar>
      {notice && <Feedback role="status">{notice}</Feedback>}
      {error && !modal && <Feedback role="alert">{error}<button type="button" onClick={() => void load()}>다시 시도</button></Feedback>}
      <TableWrap><Table>
        <thead><tr><th>이름</th><th>아이디</th><th>연락처</th><th><SortButton onClick={() => toggleSort("permission")}>권한 ↕</SortButton></th><th><SortButton onClick={() => toggleSort("team")}>팀 ↕</SortButton></th><th><SortButton onClick={() => toggleSort("status")}>상태 ↕</SortButton></th><th>관리</th></tr></thead>
        <tbody>{loading ? <tr><td colSpan={7}>계정 목록을 불러오는 중입니다.</td></tr> : visible.length === 0 ? <tr><td colSpan={7}>조건에 맞는 계정이 없습니다.</td></tr> : visible.map(item => <tr key={item.seq}>
          <td>{canManageAccounts ? <AccountButton type="button" onClick={() => openEdit(item)}>{item.name || item.nickname}</AccountButton> : item.name || item.nickname}</td><td>{item.userid}</td><td>{item.phone || "미등록"}</td><td>{item.permission_name}</td><td>{item.team_name || "없음"}</td><td><StatusBadge $status={accountStatus(item.employment_status)}>{statusLabel[accountStatus(item.employment_status)]}</StatusBadge></td>
          <td><ActionButtons>{canManageAccounts && <><SmallButton type="button" onClick={() => openEdit(item)}>계정 수정</SmallButton>{item.user_level === "cs" && <SmallButton type="button" onClick={() => { setSelected(item); setTemporaryPassword(""); setError(""); setModal("reset"); }}>비밀번호 초기화</SmallButton>}</>}</ActionButtons></td>
        </tr>)}</tbody>
      </Table></TableWrap>
      <Pagination aria-label="계정 목록 페이지"><button type="button" disabled={page === 1} onClick={() => setPage(page - 1)}>‹</button>{Array.from({ length: pages }, (_, index) => index + 1).slice(Math.max(0, page - 5), Math.max(10, page + 5)).map(number => <button type="button" key={number} aria-current={page === number ? "page" : undefined} onClick={() => setPage(number)}>{number}</button>)}<button type="button" disabled={page === pages} onClick={() => setPage(page + 1)}>›</button></Pagination>
    </Shell>

    {modal && <Backdrop data-admin-modal-overlay="" onMouseDown={() => !saving && setModal("")}><Modal data-admin-modal="" role="dialog" aria-modal="true" onMouseDown={event => event.stopPropagation()}>
      <ModalHeader data-admin-modal-header=""><h2 data-admin-modal-title="">{modal === "create" ? "계정 생성" : modal === "edit" ? "계정 수정" : modal === "settings" ? "팀/권한 설정" : "비밀번호 초기화"}</h2><ModalCloseButton tone="admin" aria-label="닫기" disabled={saving} onClick={() => setModal("")} /></ModalHeader>
      {error && <ModalError role="alert">{error}</ModalError>}
      {modal === "create" && <Form data-admin-modal-content="" onSubmit={create}><div data-admin-modal-body="" className="staff-modal-fields">
        <label><LabelText>아이디 <em>*</em></LabelText><FieldRow><input autoFocus required value={createForm.userid} onChange={event => setCreateForm({ ...createForm, userid: event.target.value.trim() })} placeholder="아이디를 입력해주세요." /><SmallButton type="button" disabled={!createForm.userid || checkingId} onClick={() => void checkId()}>{checkingId ? "확인 중" : "중복확인"}</SmallButton></FieldRow>{idCheck?.userid === createForm.userid && <span role="status" style={{ color: idCheck.available ? "var(--admin-theme-success, #276749)" : "var(--admin-theme-danger, #c53030)" }}>{idCheck.message}</span>}</label>
        <label><LabelText>비밀번호 <em>*</em></LabelText><input type="password" required minLength={8} value={createForm.userpw} onChange={event => setCreateForm({ ...createForm, userpw: event.target.value })} placeholder="비밀번호를 입력해주세요." autoComplete="new-password" /></label>
        <label><LabelText>이름 <em>*</em></LabelText><input required value={createForm.name} onChange={event => setCreateForm({ ...createForm, name: event.target.value })} placeholder="이름을 입력해주세요." /></label>
        <label>연락처<input type="tel" inputMode="tel" autoComplete="tel" pattern="01[0-9]-?[0-9]{3,4}-?[0-9]{4}" title="휴대폰 번호를 입력해주세요 (예: 010-1234-5678)." value={createForm.phone} onChange={event => setCreateForm({ ...createForm, phone: event.target.value })} placeholder="리허설 초대 문자를 받을 휴대폰 번호" /></label>
        <Grid2><label>팀<select value={createForm.team_seq} onChange={event => setCreateForm({ ...createForm, team_seq: event.target.value })}><option value="">없음</option>{teams.map(team => <option key={team.seq} value={team.seq}>{team.name}</option>)}</select></label>{canManagePermissions ? <SmallButton type="button" onClick={() => { setSettingTab("team"); setModal("settings"); }}>⚙ 설정</SmallButton> : <span />}
        <label>권한<select value={createForm.permission_seq} disabled={!canManagePermissions} onChange={event => setCreateForm({ ...createForm, permission_seq: event.target.value })}><option value="">기본 상담원</option>{permissions.map(permission => <option key={permission.seq} value={permission.seq}>{permission.name}</option>)}</select></label>{canManagePermissions ? <SmallButton type="button" onClick={() => { setSettingTab("permission"); setModal("settings"); }}>⚙ 설정</SmallButton> : <span />}</Grid2>
        </div><div data-admin-modal-footer=""><Primary type="submit" disabled={saving}>{saving ? "생성 중" : "계정 생성하기"}</Primary></div>
      </Form>}
      {modal === "edit" && selected && <Form data-admin-modal-content="" onSubmit={saveEdit}><div data-admin-modal-body="" className="staff-modal-fields">
        <label>아이디<input value={selected.userid} disabled /></label><label><LabelText>이름 <em>*</em></LabelText><input required value={editForm.name} onChange={event => setEditForm({ ...editForm, name: event.target.value })} /></label>
        <label>연락처<input type="tel" inputMode="tel" autoComplete="tel" pattern="01[0-9]-?[0-9]{3,4}-?[0-9]{4}" title="휴대폰 번호를 입력해주세요 (예: 010-1234-5678)." value={editForm.phone} onChange={event => setEditForm({ ...editForm, phone: event.target.value })} placeholder="리허설 초대 문자를 받을 휴대폰 번호" /></label>
        {selected.user_level === "cs" && <><Grid2><label>팀<select value={editForm.team_seq} onChange={event => setEditForm({ ...editForm, team_seq: event.target.value })}><option value="">없음</option>{teams.map(team => <option key={team.seq} value={team.seq}>{team.name}</option>)}</select></label><span /><label>권한<select value={editForm.permission_seq} disabled={!canManagePermissions} onChange={event => setEditForm({ ...editForm, permission_seq: event.target.value })}><option value="">기본 상담원</option>{permissions.map(permission => <option key={permission.seq} value={permission.seq}>{permission.name}</option>)}</select></label><span /></Grid2><label>상태<select value={accountStatus(editForm.employment_status)} onChange={event => setEditForm({ ...editForm, employment_status: event.target.value === "active" ? "active" : "retired" })}>{Object.entries(statusLabel).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label></>}
        <label>새 비밀번호<input type="password" value={editForm.new_password} onChange={event => setEditForm({ ...editForm, new_password: event.target.value })} placeholder="변경할 때만 입력해주세요." autoComplete="new-password" /></label>
        </div><ModalActions data-admin-modal-footer=""><Secondary type="button" onClick={() => setModal("")}>취소</Secondary><Primary type="submit" disabled={saving}>{saving ? "저장 중" : "저장하기"}</Primary></ModalActions>
      </Form>}
      {modal === "settings" && <Settings data-admin-modal-content=""><div data-admin-modal-body=""><Tabs><button type="button" aria-pressed={settingTab === "team"} onClick={() => setSettingTab("team")}>팀 설정</button><button type="button" aria-pressed={settingTab === "permission"} onClick={() => setSettingTab("permission")}>권한 설정</button></Tabs>
        {settingTab === "team" ? <><OptionList>{teams.map(option => <li key={option.seq}><span>{option.name}</span><button type="button" onClick={() => deleteOption(option)}>삭제</button></li>)}</OptionList><FieldRow><input aria-label="설정 명칭" value={settingName} onChange={event => setSettingName(event.target.value)} placeholder="팀 명칭" /><Primary type="button" disabled={!settingName.trim() || saving} onClick={saveOption}>추가</Primary></FieldRow></>
          : <><PermissionTabs>{permissions.map(option => <button type="button" key={option.seq} aria-pressed={selectedPermissionSeq === option.seq} onClick={() => selectPermission(option)}>{option.name}</button>)}</PermissionTabs>
            {selectedPermissionSeq && <PermissionEditor><EditorHeading><FieldRow><input aria-label="권한 명칭" value={permissionName} onChange={event => setPermissionName(event.target.value)} /><Secondary type="button" disabled={saving} onClick={() => { const option = permissions.find(item => item.seq === selectedPermissionSeq); if (option) deleteOption(option); }}>권한 삭제</Secondary></FieldRow></EditorHeading>
              {SELLER_ADMIN_PERMISSION_GROUPS.map(group => <PermissionGroup key={group.title}><h3>{group.title}</h3>{group.items.map(item => <label key={item.key}><input type="checkbox" checked={permissionDraft[item.key]} onChange={event => setPermissionDraft(current => ({ ...current, [item.key]: event.target.checked }))} /><span>{item.label}</span></label>)}</PermissionGroup>)}

            </PermissionEditor>}
            <AddPermission><strong>권한 추가</strong><FieldRow><input aria-label="설정 명칭" value={settingName} onChange={event => setSettingName(event.target.value)} placeholder="권한 명칭" /><Primary type="button" disabled={!settingName.trim() || saving} onClick={saveOption}>추가</Primary></FieldRow></AddPermission></>}
      </div>{settingTab === "permission" && selectedPermissionSeq && <div data-admin-modal-footer=""><Primary type="button" disabled={saving || !permissionName.trim()} onClick={savePermission}>{saving ? "저장 중" : "설정 완료"}</Primary></div>}</Settings>}
      {modal === "reset" && selected && <ResetBody data-admin-modal-content="">{temporaryPassword ? <><div data-admin-modal-body=""><p>임시 비밀번호가 발급되었습니다. 이 창을 닫으면 다시 확인할 수 없습니다.</p><Temporary><code>{temporaryPassword}</code><button type="button" onClick={() => void navigator.clipboard.writeText(temporaryPassword)}>복사</button></Temporary></div><div data-admin-modal-footer=""><Primary type="button" onClick={() => setModal("")}>확인</Primary></div></> : <><div data-admin-modal-body=""><p><strong>{selected.name}</strong> 계정의 비밀번호를 초기화하시겠습니까?</p><p>초기화 후 발급되는 임시 비밀번호를 담당자에게 전달해 주세요.</p></div><ModalActions data-admin-modal-footer=""><Secondary type="button" onClick={() => setModal("")}>취소</Secondary><Primary type="button" disabled={saving} onClick={() => void resetPassword()}>{saving ? "초기화 중" : "초기화"}</Primary></ModalActions></>}</ResetBody>}
    </Modal></Backdrop>}
  </Layout_Admin>;
}

const Shell = styled.div`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
  color: var(--admin-theme-text, #262229);
  > header { margin-bottom: 4px; }

  button:not(.admin-header-action), input, select { font-family: inherit; }
  button:focus-visible, input:focus-visible, select:focus-visible {
    outline: 2px solid #6840d8;
    outline-offset: 2px;
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
`;

const Count = styled.p`
  margin: 0;
  font-size: 14px;
  white-space: nowrap;
  strong { color: var(--admin-theme-link, #6941c6); }
`;

const SearchGroup = styled.form`
  flex: 1 1 380px;
  min-width: 0;
  max-width: 470px;
  margin-left: auto;
  display: grid;
  grid-template-columns: 100px minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;

  select {
    width: 100%;
    height: 44px;
    box-sizing: border-box;
    border: 1px solid var(--admin-theme-border, #ddd9e2);
    border-radius: 6px;
    padding: 0 32px 0 12px;
    background: var(--admin-theme-surface, #fff);
    color: var(--admin-theme-text, #3f3944);
    font-size: 14px;
  }

  @media (max-width: 760px) {
    flex-basis: 100%;
    max-width: none;
  }

  @media (max-width: 480px) {
    grid-template-columns: minmax(0, 1fr) auto;
    select { grid-column: 1 / -1; }
  }
`;

const SearchBox = styled.div`
  width: 100%;
  min-width: 0;
  height: 44px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--admin-theme-border, #ddd9e2);
  border-radius: 6px;
  padding: 0 12px;
  background: var(--admin-theme-surface, #fff);

  span { flex: none; font-size: 20px; color: var(--admin-theme-muted, #6d6671); }
  input {
    width: 100%;
    min-width: 0;
    border: 0;
    outline: 0;
    background: transparent;
    color: var(--admin-theme-text, #2f2933);
    font-size: 14px;
  }
  input::placeholder { color: var(--admin-muted); }
`;

const Filters = styled.div`
  flex: 1 1 390px;
  min-width: 0;
  max-width: 480px;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;

  select {
    width: 100%;
    min-width: 0;
    height: 44px;
    box-sizing: border-box;
    border: 1px solid var(--admin-theme-border, #e2dee7);
    border-radius: 6px;
    padding: 0 32px 0 12px;
    background: var(--admin-theme-surface, #fff);
    color: var(--admin-theme-text, #514a56);
    font-size: 14px;
  }

  @media (max-width: 760px) {
    flex-basis: 100%;
    max-width: none;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;


const Primary = styled.button`
  min-height: 42px;
  box-sizing: border-box;
  padding: 0 18px;
  border: 1px solid var(--admin-accent);
  border-radius: 9px;
  background: var(--admin-accent);
  color: #fff;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  &:hover:not(:disabled) { background: var(--admin-accent-deep); border-color: var(--admin-accent-deep); }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

const Secondary = styled.button`
  min-height: 42px;
  box-sizing: border-box;
  padding: 0 18px;
  border: 1px solid var(--admin-border);
  border-radius: 9px;
  background: var(--admin-theme-surface, #fff);
  color: var(--admin-ink);
  cursor: pointer;
  &:hover { background: var(--admin-accent-soft); border-color: var(--admin-theme-link, var(--admin-accent)); }
`;

const Feedback = styled.div`
  padding: 12px 14px;
  border-radius: 6px;
  background: var(--admin-theme-success-soft, #eef8f0);
  color: var(--admin-theme-text, #205d36);
  font-size: 13px;
  &[role="alert"] { background: var(--admin-theme-danger-soft, #fff0ee); color: var(--admin-theme-danger, #b42318); }
  button { margin-left: 10px; border: 0; background: transparent; color: inherit; text-decoration: underline; cursor: pointer; }
`;

const TableWrap = styled.div`
  width: 100%;
  min-width: 0;
  overflow: auto;
  box-sizing: border-box;
  border: 1px solid var(--admin-theme-border, #e5e7eb);
  border-radius: 8px;
  background: var(--admin-theme-surface, #fff);
`;

const Table = styled.table`
  width: 100%;
  min-width: 780px;
  border-collapse: collapse;
  table-layout: fixed;

  th, td {
    height: 56px;
    box-sizing: border-box;
    padding: 0 18px;
    border-bottom: 1px solid var(--admin-theme-border, #f0edf2);
    text-align: left;
    font-size: 14px;
    white-space: nowrap;
  }
  ${sellerTable}
  tbody tr:last-child td { border-bottom: 0; }

  th:last-child, td:last-child { width: 230px; text-align: right; }
  .sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0, 0, 0, 0); }
`;

const SortButton = styled.button`
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  cursor: pointer;
  padding: 8px 0;
`;

const AccountButton = styled.button`
  max-width: 100%;
  overflow: hidden;
  border: 0;
  background: transparent;
  padding: 8px 0;
  color: var(--admin-theme-text, #29242e);
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: underline;
  text-decoration-color: transparent;
  text-underline-offset: 3px;
  &:hover { text-decoration-color: currentColor; }
`;

const StatusBadge = styled.span<{ $status: AccountStatus }>`
  display: inline-flex;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-pill);
  background: ${({ $status }) => $status === "active" ? "var(--color-success-subtle)" : "var(--color-bg-muted)"};
  color: ${({ $status }) => $status === "active" ? "var(--color-success)" : "var(--color-text-secondary)"};
  font-size: var(--text-caption);
  font-weight: var(--font-weight-semibold);
`;

const SmallButton = styled.button`
  min-height: 34px;
  box-sizing: border-box;
  padding: 0 12px;
  border: 1px solid var(--admin-theme-border, #ded8e4);
  border-radius: 5px;
  background: var(--admin-theme-surface, #fff);
  color: var(--admin-theme-text, #423a49);
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
  &:hover { background: var(--admin-theme-raised, #faf9fb); }
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const Pagination = styled.nav`
  display: flex;
  justify-content: center;
  gap: 4px;
  button { width: 34px; height: 34px; border: 0; border-radius: 50%; background: var(--admin-theme-surface, #fff); color: var(--admin-theme-muted, #675f6d); cursor: pointer; }
  button:hover:not(:disabled) { background: var(--admin-theme-accent-soft, #f7f4fc); }
  button[aria-current="page"] { background: var(--admin-theme-accent-soft, #f0ebff); color: var(--admin-theme-link, #4b25c7); font-weight: 700; }
  button:disabled { opacity: .35; cursor: default; }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  padding: 20px;
  background: rgba(18, 15, 22, .5);
`;

const Modal = styled.div`
  width: min(518px, calc(100vw - 40px));
  max-height: calc(100dvh - 40px);
  overflow: auto;
  box-sizing: border-box;
  border-radius: 10px;
  background: var(--admin-theme-surface, #fff);
  box-shadow: 0 22px 70px rgba(20, 14, 31, .28);
`;

const ModalHeader = styled.header`
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border-bottom: 1px solid var(--admin-theme-border, #eeeaf1);
  padding: 16px 56px;
  position: relative;
  h2 { margin: 0; font-size: 22px; text-align: center; }
  button { position: absolute; right: 18px; width: 40px; height: 40px; border: 0; background: transparent; color: var(--admin-theme-text, #49414f); font-size: 30px; line-height: 1; cursor: pointer; }
`;

const ModalError = styled.p`
  margin: 14px 20px 0;
  padding: 10px 12px;
  border-radius: 5px;
  background: var(--admin-theme-danger-soft, #fff0ee);
  color: var(--admin-theme-danger, #b42318);
  font-size: 13px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 22px;
  padding: 26px 20px 20px;
  label { display: flex; flex-direction: column; gap: 9px; min-width: 0; font-size: 14px; font-weight: 600; }
  em { color: var(--admin-theme-danger, #d92d20); font-style: normal; }
  input, select { width: 100%; height: 48px; min-width: 0; box-sizing: border-box; border: 1px solid var(--admin-theme-border, #e0dbe4); border-radius: 6px; padding: 0 14px; background: var(--admin-theme-surface, #fff); color: var(--admin-theme-text, #302a34); font-size: 14px; }
  input::placeholder { color: var(--admin-muted); }
  input:disabled { background: var(--admin-theme-raised, #f7f5f8); color: var(--admin-theme-muted, #777); }
  > ${Primary} { width: 100%; height: 48px; }
`;

const LabelText = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 3px;
  width: fit-content;
`;

const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  input {
    box-sizing: border-box;
    flex: 1;
    min-width: 0;
    height: 44px;
    padding: 0 13px;
    border: 1px solid var(--admin-theme-border, #dedbe2);
    border-radius: 6px;
    background: var(--admin-theme-surface, #fff);
    color: var(--admin-theme-text, #2f2933);
    font: inherit;
  }
  input::placeholder { color: var(--admin-muted); }
  input:focus { border-color: var(--admin-theme-focus, #6941c6); outline: 2px solid var(--admin-theme-focus, rgba(105, 65, 198, .14)); }
  > button { min-height: 44px; }
`;

const Grid2 = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 10px 8px;
  > button { height: 48px; }
`;

const ModalActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 4px;
  button { height: 48px; }
`;

const Settings = styled.div`padding: 20px;`;

const Tabs = styled.div.attrs<{ 'data-admin-tabs'?: string }>({ 'data-admin-tabs': '' })`
  display: flex;
  border-bottom: 1px solid var(--admin-theme-border, #e7e1eb);
  margin-bottom: 16px;
  button { flex: 1; height: 44px; border: 0; border-bottom: 2px solid transparent; background: var(--admin-theme-surface, #fff); color: var(--admin-theme-muted, #625b66); cursor: pointer; }
  button[aria-pressed="true"] { border-color: var(--admin-theme-focus, #6941c6); color: var(--admin-theme-link, #4b25c7); font-weight: 700; }
`;

const OptionList = styled.ul`
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
  max-height: 280px;
  overflow: auto;
  li { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding: 12px; border-bottom: 1px solid var(--admin-theme-border, #f0edf2); }
  button { border: 0; background: transparent; color: var(--admin-theme-danger, #b42318); cursor: pointer; }
`;

const PermissionTabs = styled.div.attrs<{ 'data-admin-tabs'?: string }>({ 'data-admin-tabs': '' })`
  display: flex;
  gap: 6px;
  margin-bottom: 18px;
  overflow-x: auto;
  padding-bottom: 2px;
  button {
    min-width: 74px;
    height: 36px;
    border: 1px solid var(--admin-theme-border, #e1dce6);
    border-radius: 6px;
    background: var(--admin-theme-surface, #fff);
    color: var(--admin-theme-text, #5d5662);
    cursor: pointer;
  }
  button[aria-pressed="true"] { border-color: var(--admin-theme-focus, #5b36ce); background: var(--admin-theme-accent-soft, #f3efff); color: var(--admin-theme-link, #4b25c7); font-weight: 700; }
`;

const PermissionEditor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  > ${Primary} { width: 100%; height: 48px; }
`;

const EditorHeading = styled.div`
  padding-bottom: 18px;
  border-bottom: 1px solid var(--admin-theme-border, #eeeaf1);
  ${Secondary} { min-height: 44px; color: var(--admin-theme-danger, #b42318); }
`;

const PermissionGroup = styled.section`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 16px;
  h3 { grid-column: 1 / -1; margin: 0 0 2px; color: var(--admin-theme-text, #322c36); font-size: 14px; }
  label { display: flex; align-items: center; gap: 8px; color: var(--admin-theme-text, #514a56); font-size: 13px; cursor: pointer; }
  input { width: 16px; height: 16px; margin: 0; accent-color: var(--admin-theme-link, #5b36ce); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const AddPermission = styled.div`
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid var(--admin-theme-border, #eeeaf1);
  strong { display: block; margin-bottom: 10px; font-size: 14px; }
`;

const ResetBody = styled.div`
  padding: 24px 20px 20px;
  p { line-height: 1.7; color: var(--admin-theme-text, #5e5764); }
  > ${Primary} { width: 100%; height: 48px; }
`;

const Temporary = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 18px 0;
  padding: 14px;
  border-radius: 6px;
  background: var(--admin-theme-accent-soft, #f5f1ff);
  code { flex: 1; min-width: 0; overflow-wrap: anywhere; color: var(--admin-theme-link, #3f208e); font-size: 15px; }
  button { border: 1px solid var(--admin-theme-border, #cfc3ed); border-radius: 5px; background: var(--admin-theme-surface, #fff); padding: 8px 12px; cursor: pointer; }
`;
